import React from 'react';
import styled from 'styled-components';
import {
  BarChart3,
  Upload,
  PenTool,
  FileText,
  TrendingUp,
  PieChart,
  Target,
  Truck,
  User,
  LogOut,
  Store,
  Loader2,
  Info,
  ShieldCheck,
  Archive,
  type LucideIcon,
} from 'lucide-react';

// ============================================================================
// Icon Mapping
// ============================================================================

export const navIconMap = {
  dashboard: BarChart3,
  upload: Upload,
  manual: PenTool,
  invoices: FileText,
  files: FileText,
  sales: TrendingUp,
  analytics: PieChart,
  goals: Target,
  suppliers: Truck,
  profile: User,
  logout: LogOut,
  store: Store,
  loading: Loader2,
  info: Info,
  compliance: ShieldCheck,
  archive: Archive,
} as const;

export type NavIconName = keyof typeof navIconMap;

// ============================================================================
// Styled Components
// ============================================================================

const IconWrapper = styled.span<{ $size?: 'sm' | 'md' | 'lg' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: ${(props) => {
    switch (props.$size) {
      case 'sm':
        return '1rem';
      case 'lg':
        return '1.5rem';
      default:
        return '1.25rem';
    }
  }};

  svg {
    width: ${(props) => {
      switch (props.$size) {
        case 'sm':
          return '1rem';
        case 'lg':
          return '1.5rem';
        default:
          return '1.25rem';
      }
    }};
    height: ${(props) => {
      switch (props.$size) {
        case 'sm':
          return '1rem';
        case 'lg':
          return '1.5rem';
        default:
          return '1.25rem';
      }
    }};
    stroke-width: 2;
    transition: all var(--transition-fast);
  }

  /* Loading animation for Loader2 icon */
  svg[data-lucide='loader-2'] {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// ============================================================================
// Component
// ============================================================================

export interface NavIconProps {
  /** Icon name from the predefined icon map */
  name: NavIconName;
  /** Icon size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS class */
  className?: string;
  /** Accessibility label */
  'aria-label'?: string;
  /** Whether the icon should be hidden from screen readers */
  'aria-hidden'?: boolean;
}

/**
 * NavIcon - Consistent navigation icon component
 *
 * Features:
 * - Predefined icon mapping for consistent design
 * - Multiple size variants
 * - Proper accessibility attributes
 * - Smooth transitions and hover effects
 * - Optimized SVG rendering from Lucide React
 */
export const NavIcon: React.FC<NavIconProps> = ({
  name,
  size = 'md',
  className,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = false,
}) => {
  const IconComponent = navIconMap[name] as LucideIcon;

  if (!IconComponent) {
    // eslint-disable-next-line no-console
    console.warn(`NavIcon: Unknown icon name "${name}"`);
    return null;
  }

  return (
    <IconWrapper
      className={className}
      $size={size}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      <IconComponent />
    </IconWrapper>
  );
};
